import React, { useContext, useEffect, useMemo, useState } from "react";
import Modal from "../../components/Modal";
import http from "../../api/apiClient";
import { AuthContext } from "../../AuthProvider";
import { FaChalkboardTeacher } from "react-icons/fa";

type Teacher = {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  keycloakUserId?: string | null;
};

type TeacherLinkStatus = {
  linked: boolean;
  candidates: Teacher[];
};

const fullName = (teacher?: Teacher | null) =>
  [teacher?.firstName, teacher?.lastName].filter(Boolean).join(" ").trim();

const teacherLinkStyles = `
  @keyframes teacherLinkModalEnter {
    from { opacity: 0; transform: translateY(12px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .teacher-link-modal-enter {
    animation: teacherLinkModalEnter 360ms cubic-bezier(0.22, 1, 0.36, 1);
    animation-fill-mode: both;
  }

  @media (prefers-reduced-motion: reduce) {
    .teacher-link-modal-enter { animation: none !important; }
  }
`;

const TeacherLinkSetupModal: React.FC = () => {
  const { keycloak } = useContext(AuthContext);

  const currentSub = keycloak?.tokenParsed?.sub || null;
  const currentEmail =
    keycloak?.tokenParsed?.email ||
    keycloak?.tokenParsed?.preferred_username ||
    "";

  const currentName =
    keycloak?.tokenParsed?.name ||
    [keycloak?.tokenParsed?.given_name, keycloak?.tokenParsed?.family_name]
      .filter(Boolean)
      .join(" ");

  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [selectedTeacherId, setSelectedTeacherId] = useState("");
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [error, setError] = useState("");
  const [linkedByServer, setLinkedByServer] = useState(false);

  const alreadyLinked = useMemo(
    () =>
      Boolean(
        currentSub &&
          teachers.some((teacher) => teacher.keycloakUserId === currentSub)
      ),
    [currentSub, teachers]
  );

  const availableTeachers = useMemo(
    () => teachers.filter((teacher) => !teacher.keycloakUserId),
    [teachers]
  );

  const suggestedTeacher = useMemo(() => {
    const byEmail = availableTeachers.filter(
      (teacher) =>
        teacher.email &&
        currentEmail &&
        teacher.email.toLowerCase() === String(currentEmail).toLowerCase()
    );

    if (byEmail.length === 1) return byEmail[0];
    if (byEmail.length > 1) return null;

    const normalizedCurrentName = String(currentName || "")
      .trim()
      .toLowerCase();

    if (!normalizedCurrentName) return null;

    return (
      availableTeachers.find(
        (teacher) => fullName(teacher).toLowerCase() === normalizedCurrentName
      ) || null
    );
  }, [availableTeachers, currentEmail, currentName]);

  const selectedTeacher = useMemo(
    () =>
      availableTeachers.find((teacher) => teacher.id === selectedTeacherId) ||
      suggestedTeacher ||
      null,
    [availableTeachers, selectedTeacherId, suggestedTeacher]
  );

  useEffect(() => {
    let cancelled = false;

    async function loadTeachers() {
      setLoading(true);
      setError("");

      try {
        const { data } = await http.get<TeacherLinkStatus>("/teachers/link-self/status");
        if (!cancelled) {
          setLinkedByServer(Boolean(data?.linked));
          setTeachers(Array.isArray(data?.candidates) ? data.candidates : []);
        }
      } catch (err: any) {
        if (!cancelled) {
          if (err?.response?.status === 403) {
            setDismissed(true);
          } else {
            setError("Teacher profiles could not be loaded.");
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadTeachers();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedTeacherId && suggestedTeacher?.id) {
      setSelectedTeacherId(suggestedTeacher.id);
      return;
    }

  }, [availableTeachers, selectedTeacherId, suggestedTeacher]);

  const handleLink = async () => {
    if (!selectedTeacher?.id) return;

    setLinking(true);
    setError("");

    try {
      await http.patch(`/teachers/${selectedTeacher.id}/link-self`);

      setTeachers((prev) =>
        prev.map((teacher) =>
          teacher.id === selectedTeacher.id
            ? { ...teacher, keycloakUserId: currentSub }
            : teacher
        )
      );

      setDismissed(true);
    } catch (err: any) {
      if (err?.response?.status === 409) {
        setError("This teacher profile is already linked.");
      } else if (err?.response?.status === 403) {
        setError("You do not have permission to link this teacher profile.");
      } else {
        setError("The teacher profile could not be linked.");
      }
    } finally {
      setLinking(false);
    }
  };

  const shouldShow =
    !loading &&
    !dismissed &&
    !alreadyLinked &&
    !linkedByServer;

  if (!shouldShow) return null;

  return (
    <Modal
      show={shouldShow}
      type="info"
      title="Set up My environment"
      message={
        <div className="teacher-link-modal-enter space-y-5 text-left">
          <style>{teacherLinkStyles}</style>

          <div className="flex gap-4 rounded-[24px] border border-indigo-100 bg-gradient-to-r from-indigo-50 via-white to-sky-50 p-4 shadow-sm dark:border-white/10 dark:from-indigo-950/35 dark:via-slate-950 dark:to-sky-950/30">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-white bg-white text-xl text-indigo-700 shadow-sm dark:border-white/10 dark:bg-white/10 dark:text-indigo-200">
              <FaChalkboardTeacher aria-hidden="true" />
            </div>

            <p className="text-sm leading-6 text-slate-600 dark:text-slate-300">
              Your account is not linked to a teacher profile yet. Link your
              profile to see your own classrooms, parent meetings and outings.
            </p>
          </div>

          <div className="rounded-[22px] border border-slate-200/80 bg-white/95 p-4 text-slate-900 shadow-[0_14px_35px_rgba(15,23,42,0.07)] dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100">
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-indigo-600 dark:text-indigo-300">
              Select your teacher profile
            </p>

            {availableTeachers.length ? (
              <select
                value={selectedTeacher?.id || ""}
                onChange={(event) => setSelectedTeacherId(event.target.value)}
                className="mt-3 w-full rounded-2xl border border-slate-200 bg-white/90 px-4 py-2 text-sm text-slate-900 shadow-sm transition focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-white/10 dark:bg-slate-950/60 dark:text-white"
              >
                <option value="">Select a teacher profile</option>
                {availableTeachers.map((teacher) => (
                  <option key={teacher.id} value={teacher.id}>
                    {fullName(teacher) || teacher.email || teacher.id}
                    {teacher.email ? ` - ${teacher.email}` : ""}
                  </option>
                ))}
              </select>
            ) : (
              <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">
                No unlinked teacher profile matches your account email. Ask an administrator to check your teacher profile email.
              </p>
            )}

            {suggestedTeacher?.id && suggestedTeacher.id === selectedTeacher?.id && (
              <p className="mt-3 inline-flex rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-bold text-indigo-700 dark:border-indigo-400/30 dark:bg-indigo-400/10 dark:text-indigo-200">
                We think this is your teacher profile.
              </p>
            )}
          </div>

          {error && (
            <div className="rounded-2xl border border-red-100 bg-red-50 p-3 text-sm font-semibold text-red-700 dark:border-red-400/30 dark:bg-red-400/10 dark:text-red-200">
              {error}
            </div>
          )}

          <div className="flex justify-end pt-4">
            <button
              type="button"
              onClick={handleLink}
              disabled={linking || !selectedTeacher}
              className="lumen-glow-sheen inline-flex items-center justify-center rounded-full border border-indigo-200 bg-indigo-50 px-5 py-2.5 text-sm font-semibold text-indigo-700 shadow-sm transition hover:border-indigo-300 hover:text-indigo-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-60 dark:border-indigo-500/40 dark:bg-indigo-400/10 dark:text-indigo-100"
            >
              {linking ? "Linking..." : "Link this profile"}
            </button>
          </div>
        </div>
      }
      onClose={() => setDismissed(true)}
    />
  );
};

export default TeacherLinkSetupModal;
